import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import LogoutModal from '../components/common/LogoutModal';
import { useSelector, useDispatch } from 'react-redux';
import { exportScript, shareScript, unshareScript, fetchScript } from '../store/actions/scriptsActions';
import { fetchScriptSuccess } from '../store/slices/scriptsSlice';
import { logoutUser } from '../store/actions/authActions';
import { BackIcon, MobileMenuIcon, StarrIcon } from '../assets/svgs/svgs';
import { extractImportantPoints } from '../utils/formatMarkdown';
import { getUserInitials, getUserDisplayName } from '../utils/userUtils';

const Script = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { formData, topic, selectedHooks, notes, scriptData: scriptDataFromState } = location.state || {};

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [shareToLibrary, setShareToLibrary] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isTogglingShare, setIsTogglingShare] = useState(false);
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);

  const user = useSelector((state) => state.auth.user);
  const { scripts, currentScript, loadingScript, scriptError } = useSelector((state) => {
    return state.scripts;
  });

  // Get scriptId from various sources
  const scriptId = useMemo(() => {
    if (id) return id;
    if (scriptDataFromState?.scriptId) return scriptDataFromState.scriptId;
    if (scriptDataFromState?._id) return scriptDataFromState._id;
    if (scriptDataFromState?.id) return scriptDataFromState.id;
    return null;
  }, [id, scriptDataFromState]);

  const scriptFromStore = useMemo(() => {
    if (scriptDataFromState?.script) {
      const scriptFromResponse = scriptDataFromState.script;
      return {
        _id: scriptFromResponse._id,
        id: scriptFromResponse._id,
        scriptId: scriptFromResponse._id,
        content: scriptFromResponse.content,
        Title: scriptFromResponse.Title,
        Introduction: scriptFromResponse.Introduction,
        Sections: scriptFromResponse.Sections,
        Conclusion: scriptFromResponse.Conclusion,
        CallToAction: scriptFromResponse.CallToAction,
        researchId: scriptDataFromState.researchId
      };
    }
    if (currentScript) {
      const currentScriptId = currentScript?.scriptId || currentScript?.id || currentScript?._id;
      if (scriptId && String(currentScriptId) === String(scriptId)) {
        return currentScript;
      }
    }
    if (scriptId) {
      return scripts.find(script => {
        const sId = script?.scriptId || script?.id || script?._id;
        return String(sId) === String(scriptId);
      });
    }
    return null;
  }, [scriptId, scripts, currentScript, scriptDataFromState]);


  useEffect(() => {
    setHasAttemptedFetch(false);
  }, [scriptId]);

  useEffect(() => {
    if (scriptDataFromState?.script && !scriptFromStore) {
      return;
    }
    if (scriptId && !loadingScript && !scriptDataFromState?.script && !hasAttemptedFetch) {
      setHasAttemptedFetch(true);
      dispatch(fetchScript(scriptId)).catch((error) => {
        console.error('Error fetching script:', error);
      });
    }
  }, [scriptId, dispatch, scriptDataFromState, scriptFromStore, loadingScript, hasAttemptedFetch]);

  useEffect(() => {
    if (scriptFromStore && scriptFromStore.isShared !== undefined) {
      setShareToLibrary(scriptFromStore.isShared);
    }
  }, [scriptFromStore]);

  const defaultTopic = {
    title: "What's Redefining Wildlife Photography in 2025",
    description: "A breakdown of the innovations and trends elevating how creators capture nature in 2025.",
    region: "US",
    category: "Wildlife",
    score: 86,
    keywords: ["Low light", "Wildlife cameras", "New trend"]
  };

  const defaultHook = "From AI-powered editing to ultra-close cinematic shots, 2025 is bringing a revolution to wildlife photography!";

  const defaultPoints = `Wildlife photography has always required patience and passion, but in 2025, new technology and creative techniques are transforming how we capture nature

1. AI Editing
2. Ultra-Close Shots
3. Drone Photography
4. Creative Storytelling`;

  const scriptData = useMemo(() => {
    if (scriptFromStore) {
      const storeTopic = scriptFromStore.topic || {};
      const storeRegion = scriptFromStore?.topic?.region || scriptFromStore?.research?.region || '';
      const storeCategory = scriptFromStore?.topic?.category || scriptFromStore?.research?.filters?.category || '';
      let hookText = '';
      if (scriptFromStore.Introduction) {
        hookText = scriptFromStore.Introduction;
      } else if (scriptFromStore.sequence?.hook?.text) {
        hookText = scriptFromStore.sequence.hook.text;
      } else if (scriptFromStore.hook) {
        if (typeof scriptFromStore.hook === 'string') {
          hookText = scriptFromStore.hook;
        } else if (scriptFromStore.hook.text) {
          hookText = scriptFromStore.hook.text;
        } else if (Array.isArray(scriptFromStore.hook) && scriptFromStore.hook[0]?.text) {
          hookText = scriptFromStore.hook[0].text;
        }
      } else if (scriptFromStore.hooks && Array.isArray(scriptFromStore.hooks) && scriptFromStore.hooks.length > 0) {
        hookText = scriptFromStore.hooks[0].text || '';
      } else if (scriptFromStore.content) {
        if (typeof scriptFromStore.content === 'string') {
          try {
            const parsed = JSON.parse(scriptFromStore.content);
            if (parsed?.script?.introduction) {
              hookText = parsed.script.introduction;
            } else {
              const hookMatch = scriptFromStore.content.match(/^hook:?\s*(.+)/i);
              if (hookMatch) {
                hookText = hookMatch[1].trim();
              }
            }
          } catch (e) {
            const hookMatch = scriptFromStore.content.match(/^hook:?\s*(.+)/i);
            if (hookMatch) {
              hookText = hookMatch[1].trim();
            }
          }
        }
      }
      
      let importantPointsText = '';
      if (scriptFromStore.content) {
        if (typeof scriptFromStore.content === 'string') {
          importantPointsText = scriptFromStore.content;
        } else if (Array.isArray(scriptFromStore.content)) {
          importantPointsText = scriptFromStore.content.map(item => 
            typeof item === 'string' ? item : item.text || ''
          ).join('\n');
        }
      } else if (scriptFromStore.importantPoints) {
        if (typeof scriptFromStore.importantPoints === 'string') {
          // Try to parse as JSON if it looks like JSON
          try {
            const parsed = JSON.parse(scriptFromStore.importantPoints);
            if (typeof parsed === 'object' && !Array.isArray(parsed) && parsed.importantPoints) {
              // Handle nested importantPoints object
              const pointsObj = parsed.importantPoints;
              const sections = [];
              
              if (pointsObj.visualCuesAndSceneIdeas?.length > 0) {
                sections.push('VISUAL CUES & SCENE IDEAS');
                pointsObj.visualCuesAndSceneIdeas.forEach((item, idx) => {
                  sections.push(`${idx + 1}. ${item}`);
                });
                sections.push('');
              }
              
              if (pointsObj.stepByStepDemonstrations?.length > 0) {
                sections.push('STEP BY STEP DEMONSTRATIONS');
                pointsObj.stepByStepDemonstrations.forEach((item, idx) => {
                  sections.push(`${idx + 1}. ${item}`);
                });
                sections.push('');
              }
              
              if (pointsObj.storytellingAndRelatableExamples?.length > 0) {
                sections.push('STORYTELLING & RELATABLE EXAMPLES');
                pointsObj.storytellingAndRelatableExamples.forEach((item, idx) => {
                  sections.push(`${idx + 1}. ${item}`);
                });
                sections.push('');
              }
              
              if (pointsObj.engagementAndPacingEnhancers?.length > 0) {
                sections.push('ENGAGEMENT & PACING ENHANCERS');
                pointsObj.engagementAndPacingEnhancers.forEach((item, idx) => {
                  sections.push(`${idx + 1}. ${item}`);
                });
                sections.push('');
              }
              
              if (pointsObj.additionalNotes?.length > 0) {
                sections.push('ADDITIONAL NOTES');
                pointsObj.additionalNotes.forEach((item, idx) => {
                  sections.push(`${idx + 1}. ${item}`);
                });
              }
              
              importantPointsText = sections.join('\n');
            } else {
              importantPointsText = scriptFromStore.importantPoints;
            }
          } catch (e) {
            // Not JSON, use as plain string
            importantPointsText = scriptFromStore.importantPoints;
          }
        } else if (typeof scriptFromStore.importantPoints === 'object' && !Array.isArray(scriptFromStore.importantPoints)) {
          // Handle JSON object format
          const pointsObj = scriptFromStore.importantPoints.importantPoints || scriptFromStore.importantPoints;
          const sections = [];
          
          if (pointsObj.visualCuesAndSceneIdeas?.length > 0) {
            sections.push('VISUAL CUES & SCENE IDEAS');
            pointsObj.visualCuesAndSceneIdeas.forEach((item, idx) => {
              sections.push(`${idx + 1}. ${item}`);
            });
            sections.push('');
          }
          
          if (pointsObj.stepByStepDemonstrations?.length > 0) {
            sections.push('STEP BY STEP DEMONSTRATIONS');
            pointsObj.stepByStepDemonstrations.forEach((item, idx) => {
              sections.push(`${idx + 1}. ${item}`);
            });
            sections.push('');
          }
          
          if (pointsObj.storytellingAndRelatableExamples?.length > 0) {
            sections.push('STORYTELLING & RELATABLE EXAMPLES');
            pointsObj.storytellingAndRelatableExamples.forEach((item, idx) => {
              sections.push(`${idx + 1}. ${item}`);
            });
            sections.push('');
          }
          
          if (pointsObj.engagementAndPacingEnhancers?.length > 0) {
            sections.push('ENGAGEMENT & PACING ENHANCERS');
            pointsObj.engagementAndPacingEnhancers.forEach((item, idx) => {
              sections.push(`${idx + 1}. ${item}`);
            });
            sections.push('');
          }
          
          if (pointsObj.additionalNotes?.length > 0) {
            sections.push('ADDITIONAL NOTES');
            pointsObj.additionalNotes.forEach((item, idx) => {
              sections.push(`${idx + 1}. ${item}`);
            });
          }
          
          importantPointsText = sections.join('\n');
        } else if (Array.isArray(scriptFromStore.importantPoints)) {
          importantPointsText = scriptFromStore.importantPoints.map(item => 
            typeof item === 'string' ? item : item.text || ''
          ).join('\n');
        }
      }
      
      return {
        hook: hookText,
        importantPoints: importantPointsText,
        topic: {
          title: scriptFromStore.Title || storeTopic.title || scriptFromStore.title || scriptFromStore.name || 'Untitled Script',
          description: storeTopic.description || scriptFromStore.description || (typeof scriptFromStore.content === 'string' ? scriptFromStore.content : '') || '',
          region: Array.isArray(storeRegion) ? storeRegion.join(', ') : (typeof storeRegion === 'string' ? storeRegion : 'Region & Category'),
          category: typeof storeCategory === 'string' ? storeCategory : '',
          score: storeTopic.score || scriptFromStore.score || 0,
          keywords: Array.isArray(storeTopic.keywords) ? storeTopic.keywords : (Array.isArray(scriptFromStore.keywords) ? scriptFromStore.keywords : [])
        },
        imageUrl: null
      };
    } else if (topic || selectedHooks) {
      return {
        hook: selectedHooks && selectedHooks[0] ? (typeof selectedHooks[0] === 'string' ? selectedHooks[0] : selectedHooks[0].text) : '',
        importantPoints: '',
        topic: topic || {
          title: '',
          description: '',
          region: '',
          category: '',
          score: 0,
          keywords: []
        }
      };
    } else {
      return {
        hook: '',
        importantPoints: '',
        topic: {
          title: '',
          description: '',
          region: '',
          category: '',
          score: 0,
          keywords: []
        },
        imageUrl: null
      };
    }
  }, [scriptFromStore, topic, selectedHooks]);

  const [hookText, setHookText] = useState('');
  const [openLoopText, setOpenLoopText] = useState('');
  const [retentionMomentText, setRetentionMomentText] = useState('');
  const [ctaPlacementText, setCtaPlacementText] = useState('');
  const [pointsText, setPointsText] = useState('');


  useEffect(() => {
    if (!scriptFromStore) return;
    
    if (scriptFromStore.Introduction) {
      setHookText(scriptFromStore.Introduction);
    } else if (scriptFromStore.sequence) {
      if (scriptFromStore.sequence.hook?.text) {
        setHookText(scriptFromStore.sequence.hook.text);
      }
      if (scriptFromStore.sequence.open_loop?.text) {
        setOpenLoopText(scriptFromStore.sequence.open_loop.text);
      }
      if (scriptFromStore.sequence.retention_moment?.text) {
        setRetentionMomentText(scriptFromStore.sequence.retention_moment.text);
      }
      if (scriptFromStore.sequence.cta_placement?.text) {
        setCtaPlacementText(scriptFromStore.sequence.cta_placement.text);
      }
    }
    
    if (scriptFromStore.content) {
      let contentToExtract = scriptFromStore.content;
      if (typeof contentToExtract === 'string') {
        try {
          const parsed = JSON.parse(contentToExtract);
          if (parsed && typeof parsed === 'object') {
            contentToExtract = parsed;
          }
        } catch (e) {
        }
      }
      
      if (typeof contentToExtract === 'object' && contentToExtract.script) {
        const extractedPoints = extractImportantPoints(JSON.stringify(contentToExtract));
        if (extractedPoints) {
          setPointsText(extractedPoints);
        } else {
          setPointsText('');
        }
      } else {
        const extractedPoints = extractImportantPoints(scriptFromStore.content);
        if (extractedPoints) {
          setPointsText(extractedPoints);
        } else {
          setPointsText('');
        }
      }
    } else {
      setPointsText('');
    }
  }, [scriptFromStore]);

  const handleViewDetailsInHook = () => {
    navigate('/script-details', {
      state: {
        formData,
        topic: scriptData.topic,
        selectedHooks: [{ text: hookText }],
        notes,
        hookText,
        pointsText
      }
    });
  };

  const handleExportScript = () => {
    if (id) {
      dispatch(exportScript(id));
      return;
    }
    
    const keywordsText = scriptData.topic.keywords && scriptData.topic.keywords.length > 0 
      ? scriptData.topic.keywords.join(', ') 
      : 'N/A';
    const content = `MY SCRIPT\n=========\n\nHook 1:\n${hookText}\n\nImportant Points:\n${pointsText}\n\nTOPIC INFORMATION\n=================\nTitle: ${scriptData.topic.title}\nDescription: ${scriptData.topic.description || 'N/A'}\nRegion: ${scriptData.topic.region || 'N/A'}\nCategory: ${scriptData.topic.category || 'N/A'}\nScore: ${scriptData.topic.score || 0}/10\nKeywords: ${keywordsText}`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'my-script.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSaveScript = () => {
    const savedScript = {
      id: Date.now(),
      title: scriptData.topic.title,
      description: scriptData.topic.description,
      hook: hookText,
      importantPoints: pointsText,
      region: scriptData.topic.region,
      category: scriptData.topic.category,
      score: scriptData.topic.score,
      keywords: scriptData.topic.keywords,
      status: 'Available',
      createdAt: new Date().toISOString(),
      shareToLibrary: shareToLibrary
    };

    const scripts = JSON.parse(localStorage.getItem('scripts') || '[]');
    scripts.push(savedScript);
    localStorage.setItem('scripts', JSON.stringify(scripts));

    if (shareToLibrary) {
      const libraryTopics = JSON.parse(localStorage.getItem('libraryTopics') || '[]');
      libraryTopics.push(savedScript);
      localStorage.setItem('libraryTopics', JSON.stringify(libraryTopics));
    }

    alert('Script saved successfully!');
    navigate('/my-scripts');
  };

  const handleShareToggle = async () => {
    if (!scriptId || isTogglingShare) return;
    
    const currentShareState = shareToLibrary;
    const newShareState = !currentShareState;
    
    // Optimistically update UI
    setShareToLibrary(newShareState);
    setIsTogglingShare(true);
    
    try {
      let success = false;
      if (newShareState) {
        // Share the script
        success = await dispatch(shareScript(scriptId));
      } else {
        // Unshare the script
        success = await dispatch(unshareScript(scriptId));
      }
      
      // If action failed, revert the optimistic update
      // Note: On success, fetchScripts() is called in the action which will update the store
      // and the useEffect will sync shareToLibrary with scriptFromStore.isShared
      if (success === false) {
        setShareToLibrary(currentShareState);
      }
    } catch (error) {
      // Revert on error
      setShareToLibrary(currentShareState);
      console.error('Error toggling share:', error);
    } finally {
      setIsTogglingShare(false);
    }
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    dispatch(logoutUser());
    navigate('/signin');
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  return (
    <div className="h-screen bg-main flex overflow-hidden">
      {sidebarOpen && (
        <div
          className="fixed inset-0 overlay-dark z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        activePage="my-scripts"
        onLogout={handleLogout}
      />

      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        <header className="bg-primary text-white py-4 px-6 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <MobileMenuIcon/>
              </button>
              
              <button 
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 px-4 py-2 border-2 border-white rounded-lg hover:bg-white/10 transition-colors"
              >
                <BackIcon/>
                <span className="font-medium text-sm">Back</span>
              </button>
            </div>

            <div className="flex items-center gap-3 ml-auto">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm border-2 border-white/30">
                {getUserInitials(user)}
              </div>
              <span className="text-sm font-medium hidden sm:inline">{getUserDisplayName(user)}</span>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h1 className="text-3xl font-bold text-gray-900 mb-1">My Script</h1>
            <p className="text-gray-600 mb-6">Based on your data here are most relevant and high performing topics</p>
            
            {loadingScript && !scriptDataFromState?.script ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : scriptError && !scriptFromStore && !scriptDataFromState?.script ? (
              <div className="flex flex-col items-center justify-center h-64">
                <div className="text-red-600 mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Script Not Found</h3>
                <p className="text-gray-600 mb-4">{scriptError}</p>
                <button
                  onClick={() => navigate('/my-scripts')}
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors"
                >
                  Go to My Scripts
                </button>
              </div>
            ) : !scriptFromStore && !scriptDataFromState?.script ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.5fr] gap-6">
              <div className="max-w-md">
                <div className="topic-card">
                  <h2 className="text-base font-semibold text-gray-800 mb-2">
                    <strong>{typeof scriptData.topic.title === 'string' ? scriptData.topic.title : String(scriptData.topic.title || 'Untitled Script')}</strong>
                  </h2>
                  <p className="text-sm text-gray-600 mb-4">
                    {typeof scriptData.topic.description === 'string' ? scriptData.topic.description : String(scriptData.topic.description || '')}
                  </p>

                  <div className="mb-4 rounded-xl overflow-hidden" style={{ border: '1px solid var(--color-primary-lighter)' }}>
                    <div className="w-full h-64 bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-primary-hover)] flex items-center justify-center">
                      <svg className="w-24 h-24 text-white/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-bold text-gray-900">Region & Category</span>
                        <div className="flex items-center gap-1">
                          <StarrIcon className="w-4 h-4 text-gray-700 stroke-current fill-none stroke-2" />
                          <span className="text-sm font-medium">{scriptData.topic.score}/10 Score</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {scriptData.topic.region && (
                          <span className="tag-brown">
                            {typeof scriptData.topic.region === 'string' ? scriptData.topic.region : String(scriptData.topic.region)}
                          </span>
                        )}
                        {scriptData.topic.category && (
                          <span className="tag-brown">
                            {typeof scriptData.topic.category === 'string' ? scriptData.topic.category : String(scriptData.topic.category)}
                          </span>
                        )}
                      </div>
                    </div>

                    {scriptData.topic.keywords && scriptData.topic.keywords.length > 0 && (
                      <div>
                        <span className="text-sm font-bold text-gray-900 block mb-2">Keyword</span>
                        <div className="flex flex-wrap gap-2">
                          {scriptData.topic.keywords.map((keyword, idx) => {
                            const keywordText = typeof keyword === 'string' ? keyword : (keyword?.text || keyword?.name || String(keyword));
                            return (
                              <span key={idx} className="tag-brown">
                                {keywordText}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-6">
                <div className="flex items-center gap-3">
                  <svg className="w-6 h-6 text-gray-900" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                  </svg>
                  <h2 className="text-lg font-semibold text-gray-900">Script</h2>
                </div>

                <div>
                  <h3 className="text-sm font-bold text-gray-900 mb-3">Hook:</h3>
                  <div className="border-2 rounded-xl p-5" style={{ 
                    borderColor: 'var(--color-primary-lighter)', 
                    backgroundColor: 'var(--color-primary-light)' 
                  }}>
                    <p className="text-sm text-gray-700 leading-relaxed">{hookText}</p>
                  </div>
                </div>

                {openLoopText && (
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 mb-3">Open Loop:</h3>
                    <div className="border-2 rounded-xl p-5" style={{ 
                      borderColor: 'var(--color-primary-lighter)', 
                      backgroundColor: 'var(--color-primary-light)' 
                    }}>
                      <p className="text-sm text-gray-700 leading-relaxed">{openLoopText}</p>
                    </div>
                  </div>
                )}

                {retentionMomentText && (
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 mb-3">Retention Moment:</h3>
                    <div className="border-2 rounded-xl p-5" style={{ 
                      borderColor: 'var(--color-primary-lighter)', 
                      backgroundColor: 'var(--color-primary-light)' 
                    }}>
                      <p className="text-sm text-gray-700 leading-relaxed">{retentionMomentText}</p>
                    </div>
                  </div>
                )}

                {ctaPlacementText && (
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 mb-3">Call to Action:</h3>
                    <div className="border-2 rounded-xl p-5" style={{ 
                      borderColor: 'var(--color-primary-lighter)', 
                      backgroundColor: 'var(--color-primary-light)' 
                    }}>
                      <p className="text-sm text-gray-700 leading-relaxed">{ctaPlacementText}</p>
                    </div>
                  </div>
                )}

                {pointsText && (
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 mb-3">Important Points</h3>
                    <div className="border-2 rounded-xl p-5" style={{ 
                      borderColor: 'var(--color-primary-lighter)', 
                      backgroundColor: 'var(--color-primary-light)' 
                    }}>
                      <div className="text-sm text-gray-700 leading-relaxed">
                        {pointsText ? (
                        <>
                          {pointsText.split('\n').map((line, index) => {
                            const trimmedLine = line.trim();
                            if (!trimmedLine) return null;
                            
                            const isNumberedItem = /^\d+\.\s/.test(trimmedLine);
                            
                            if (isNumberedItem) {
                              return (
                                <div key={index} className="mb-3">
                                  <p className="font-medium">{trimmedLine}</p>
                                </div>
                              );
                            }
                            
                            return (
                              <div key={index} className="mb-2">
                                {trimmedLine}
                              </div>
                            );
                          }).filter(Boolean)}
                          {scriptId && (
                            <div className="mt-3 text-center">
                              <button 
                                onClick={() => {
                                  navigate('/script-details', {
                                    state: {
                                      script: scriptFromStore,
                                      scriptId: scriptId
                                    }
                                  });
                                }}
                                className="text-sm font-medium underline"
                                style={{ color: 'var(--color-primary)' }}
                              >
                                View details
                              </button>
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-gray-500 italic">No important points available</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="rounded-2xl p-5" style={{ 
                  backgroundColor: 'var(--color-primary-light)', 
                  border: '1px solid var(--color-primary-lighter)' 
                }}>
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-sm text-gray-700">You want to share your script to the shared library?</p>
                    <button 
                      type="button"
                      onClick={handleShareToggle}
                      disabled={isTogglingShare || !id}
                      className={`relative inline-flex h-7 w-12 items-center rounded-full transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed ${
                        shareToLibrary 
                          ? 'bg-primary' 
                          : 'bg-gray-300'
                      }`}
                    >
                      <span 
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-all duration-200 ease-in-out ${
                          shareToLibrary ? 'translate-x-6' : 'translate-x-1'
                        }`} 
                      />
                    </button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                     onClick={() => {
                       if (scriptId) {
                         dispatch(exportScript(scriptId));
                       } else {
                         handleExportScript();
                       }
                     }}
                    className="font-semibold text-base text-white transition-all bg-[#653600] hover:bg-[#4a2800] shadow-sm"
                    style={{ width: '402px', height: '56px', borderRadius: '10px' }}
                  >
                    Export Script
                  </button>
                </div>
              </div>
            </div>
            )}
            <LogoutModal 
              showLogoutModal={showLogoutModal}
              onConfirm={confirmLogout}
              onCancel={cancelLogout}
            />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Script;